/**
 * Script Delay Loader
 *
 * Two cooperating systems in one file:
 *
 * 1. Tagged scripts (the theme's own feature): scripts the theme writes with a
 *    `delay=`/`await=` attribute are held until first user interaction or a 5s
 *    timeout, with optional per-script delay, dependency chains, viewport-lazy
 *    loading, and module type.
 *
 * 2. DOM interception (a 1:1 port of Yottaa Rapid+JS's sequencing, as captured in
 *    references/yottaa-scripts/): scripts injected at runtime by GTM tags,
 *    content_for_header apps, and vendor SDKs — which the theme can never tag —
 *    are neutralized IN PLACE at insertion and released at window.load with a
 *    per-host stagger. The mechanics deliberately mirror the captured runtime:
 *
 *    - Capture surfaces: appendChild/insertBefore/append are patched on
 *      document.head and document.body (Yottaa patches body; head is the same
 *      mechanism with strictly better coverage since this theme's third parties
 *      mostly target head), plus a document-wide MutationObserver (childList +
 *      subtree) for scripts inserted anywhere else. Yottaa's per-element `src`
 *      setter override is NOT ported — the captured runtime only arms it on IE.
 *    - Neutralize in place: the vendor's own element is kept. Its `src` is
 *      stashed into a data attribute and removed, and its `type` is rewritten to
 *      a non-executable marker (Yottaa: `yo/sequence/...`; here:
 *      `text/delay-sequenced`) before the real DOM method inserts it. Because
 *      the node enters the document with no src and no children, the browser
 *      never prepares it, so restoring `type` and re-setting the src ATTRIBUTE
 *      later re-triggers preparation on the SAME node — the vendor's onload/
 *      onerror handlers and every other attribute survive untouched. This is
 *      exactly Yottaa's stash/restore (`yo-src` + `attributesrc`) mechanism,
 *      and it's why no currentScript heuristics or per-vendor exemption hacks
 *      are needed: a vendor's internal follow-up loads keep their own element,
 *      type, and handlers.
 *    - Release: everything captured is released when window.load fires
 *      (Yottaa executes its delayed list on `core/load`), each entry after its
 *      spread offset. Anything captured AFTER load releases immediately on its
 *      offset, and the scheduler expires 6s after load
 *      (Yottaa `lastScheduleTime: 6000`) — later insertions pass through
 *      completely untouched.
 *    - Spread offsets (Yottaa `Yo.schedule.spread(10)` with `last: 100`): the
 *      offset counter starts at 100ms and grows 10ms per captured resource
 *      whose host hasn't started loading yet; a host counts as "started" once
 *      its first resource is released.
 *    - Fail open, always: on any doubt (no src, exempt, expired window,
 *      disconnected node, thrown error) the script loads normally. A script
 *      that loads immediately when it could have waited is a minor perf
 *      regression; a script that never loads is a broken page.
 *
 * HTML Attributes (tagged scripts only):
 * - delay="url"            : URL of script to load after user interaction or timeout
 * - await="url"            : Alternative to delay, used with ID-based chained loading
 * - delayed-time="ms"      : Additional milliseconds to wait before loading
 * - run-after-load="ids"   : Comma-separated script IDs to load after this script
 * - data-module="true"     : Inject as type="module" (needed for scripts using ES6
 *                            imports). Off by default: module scripts are fetched with
 *                            CORS, which most third-party hosts don't allow, and module
 *                            scope breaks legacy UMD/global-var scripts.
 * - data-lazy-load="true"  : Don't load on interaction/timeout at all — wait until the
 *                            tag scrolls near the viewport (IntersectionObserver), or
 *                            until the window-load release fires, whichever is first.
 *                            Only meaningful when the tag has a real visual anchor
 *                            (the observer watches its next sibling, or its parent).
 *
 * Exemptions:
 * - EXEMPT_PATTERNS is this store's captured Yottaa allowlist
 *   (`Yo.sequence.resource(pattern).none()` calls in configure.rapid.js),
 *   verbatim. Matching is substring (Yottaa keys patterns as `*pattern*`).
 *   A matching script is never intercepted; a matching tagged script loads as
 *   soon as the loader is triggered, skipping delayed-time.
 *
 * Events (dispatched on document, detail = { src, id }):
 * - delay:script-loaded : a tagged script finished loading
 * - delay:script-error  : a tagged script failed (404, ad-blocker, offline); its
 *                         run-after-load chain still fires so dependents aren't orphaned
 *   (Intercepted scripts keep the vendor's own element and event handlers, so they
 *   don't need these events.)
 *
 * Usage Examples:
 *
 * Basic:
 *   <script delay="https://example.com/analytics.js"></script>
 *
 * With extra delay:
 *   <script delay="https://example.com/script.js" delayed-time="3000"></script>
 *
 * Chained loading:
 *   <script id="main" delay="https://example.com/main.js"></script>
 *   <script id="dependent" await="https://example.com/lib.js" run-after-load="main"></script>
 *
 * Lazy / below-the-fold:
 *   <script delay="https://example.com/reviews-widget.js" data-lazy-load="true"></script>
 *
 * Enable/disable (the `delay_loader_enabled` theme setting):
 *   Handled entirely in theme.liquid, the same way `yottaa_enabled` gates the Yottaa
 *   tag: when the setting is off, this file's tag is not rendered at all, and every
 *   delay/await-coded tag renders as a plain <script src> tag instead — so nothing
 *   here needs (or has) a runtime flag.
 */
(function (window) {
  // How long to wait before auto-loading tagged scripts if no user interaction occurs (in seconds)
  const autoLoadDuration = 5;

  // Spread scheduling constants, matching the captured Yottaa runtime:
  // g.scheduling = { last: 100, step: 100 } with the step overridden to 10 by the
  // dashboard's Yo.schedule.spread(10). Offsets run 110, 120, 130, ...
  const spreadBaseMs = 100;
  const spreadStepMs = 10;

  // How long after window.load the interception scheduler keeps working
  // (Yottaa `lastScheduleTime: 6000`). Captures after this pass through untouched.
  const scheduleWindowMs = 6000;

  // Non-executable type marker for neutralized scripts (Yottaa: "yo/sequence/<type>/<value>").
  const NEUTRALIZED_TYPE = "text/delay-sequenced";

  // This store's captured Yottaa exemption allowlist (configure.rapid.js), verbatim.
  // Substring-matched, same as Yottaa's `*pattern*` keys. Never delay these.
  const EXEMPT_PATTERNS = [
    "//" + window.location.hostname,
    "about:blank",
    "javascript:",
    ".shopify.com",
    ".shopifycloud.com",
    ".shopifysvc.com",
    "cdn.shopify.com",
    "shopify.com",
    "us.myshopify.com",
    "shop.app",
    "jquery",
    "googletagmanager",
    "analytics.js",
    "google-analytics.com",
    "uselayers.com"
  ];

  // User interaction events that trigger tagged-script loading
  const eventList = ["keydown", "mousemove", "wheel", "touchmove", "touchstart", "touchend"];

  // Fallback timer to load tagged scripts after 5 seconds even without interaction
  const autoLoadTimeout = setTimeout(runScripts, autoLoadDuration * 1000);

  // Tracks injected tagged scripts by src so duplicates aren't re-requested. Each entry:
  // { element, settled } — settled is true once load or error has fired, letting a
  // duplicate request still run its callback (its run-after-load chain) either
  // immediately or by piggybacking on the in-flight element's events.
  const scriptsLoaded = new Map();

  window.addEventListener("load", onWindowLoad, { once: true });

  // Set up listeners for user interaction events
  eventList.forEach(function (event) {
    window.addEventListener(event, triggerScripts, { passive: true });
  });

  /**
   * Triggers tagged-script loading and removes event listeners
   * Called on first user interaction
   */
  function triggerScripts() {
    runScripts();
    clearTimeout(autoLoadTimeout);
    eventList.forEach(function (event) {
      window.removeEventListener(event, triggerScripts, { passive: true });
    });
  }

  /**
   * Extracts the hostname from a URL string. Falls back to the raw string
   * (relative/asset URLs, malformed URLs) so those still get a stable bucket.
   * @param {string} src
   * @returns {string}
   */
  function getHost(src) {
    try {
      return new URL(src, window.location.href).hostname || src;
    } catch (e) {
      return src;
    }
  }

  /**
   * Whether a URL matches one of the exemption patterns and should skip delay entirely.
   * @param {string} src
   * @returns {boolean}
   */
  function isExempt(src) {
    return EXEMPT_PATTERNS.some(function (pattern) {
      return pattern && src.indexOf(pattern) !== -1;
    });
  }

  /**
   * Loads a tagged script asynchronously, deduplicating by src. On duplicate requests
   * the callback still runs (immediately if already settled, else on the in-flight
   * element's load/error), so a second tag's run-after-load chain isn't dropped.
   * A load failure (404, ad-blocker, offline) dispatches delay:script-error and
   * still walks the callback so dependent scripts aren't orphaned.
   * @param {string} src - The URL of the script to load
   * @param {Function} callback - Optional callback to run after the script settles
   * @param {HTMLElement} scriptTag - The originating script tag, if any
   */
  const loadScript = (src, callback, scriptTag) => {
    // Entries are recorded synchronously, before the request goes out, so two racing
    // scheduling paths (e.g. the interaction trigger and the window.load release)
    // can't both append the script.
    const existing = scriptsLoaded.get(src);
    if (existing) {
      if (!callback) return;
      if (existing.settled) {
        callback(null);
      } else {
        existing.element.addEventListener("load", () => callback(null));
        existing.element.addEventListener("error", () => callback(null));
      }
      return;
    }

    const createdElement = document.createElement("script");
    const entry = { element: createdElement, settled: false };
    scriptsLoaded.set(src, entry);

    if (scriptTag?.getAttribute("data-module") === "true") {
      createdElement.type = "module"; // ES6 imports; fetched with CORS
    } else {
      createdElement.async = true;
    }
    createdElement.__delayManaged = true; // interception skips our own elements
    createdElement.src = src;

    const settle = (eventName) => {
      entry.settled = true;
      const scriptEventDetail = { src, id: scriptTag?.id || null };

      document.dispatchEvent(new CustomEvent(eventName, { detail: scriptEventDetail }));

      if (eventName === "delay:script-loaded" && src.includes("flickity.pkgd.min.js")) {
        window.__flickityReady = true;
        document.dispatchEvent(new CustomEvent("flickity:loaded", { detail: scriptEventDetail }));
      }

      if (callback) callback(null);
    };

    createdElement.addEventListener("load", () => settle("delay:script-loaded"));
    createdElement.addEventListener("error", () => settle("delay:script-error"));

    insertRealScript(createdElement);
  };

  // Set once document.body is patched, to the *unpatched* appendChild — lets loadScript()
  // insert its own elements without even touching the interception path.
  let insertRealScript = (el) => document.body.appendChild(el);

  /**
   * Gets a script tag by ID and initiates its loading
   * Used for chained script loading via run-after-load attribute
   * @param {string} id - The ID of the script tag to load
   */
  function getAwaitScriptTag(id) {
    const tag = document.getElementById(id);
    if (!tag) return;
    tagsHandled.add(tag); // so the window.load await-sweep skips it
    const src = tag.getAttribute("await");
    loadWithDelay(tag, src);
  }

  /**
   * Applies a tagged script's explicit delayed-time before loading it. Exempt
   * (first-party / allowlisted) scripts skip even that and load immediately.
   * @param {HTMLElement} tag - The script tag element
   * @param {string} src - The URL of the script to load
   */
  function loadWithDelay(tag, src) {
    if (!src) return; // blank delay/await attr would otherwise request the page itself

    if (isExempt(src)) {
      loadScript(src, () => getRunAfterLoadAttribute(tag), tag);
      return;
    }

    const delayedTimeAttribute = tag.getAttribute("delayed-time") || 0;
    const explicitDelay = parseInt(delayedTimeAttribute, 10) || 0;

    setTimeout(() => {
      loadScript(src, () => getRunAfterLoadAttribute(tag), tag);
    }, explicitDelay);
  }

  /**
   * Loads any scripts that should run after this script completes
   * Enables sequential loading of dependent scripts
   * @param {HTMLElement} scriptTag - The script tag with run-after-load attribute
   */
  function getRunAfterLoadAttribute(scriptTag) {
    const newScriptId = scriptTag.getAttribute("run-after-load");
    if (!newScriptId) return;

    const scriptsToLoad = newScriptId.split(",");
    scriptsToLoad.forEach((singleScript) => {
      getAwaitScriptTag(singleScript.trim());
    });
  }

  /**
   * Sets up an IntersectionObserver-based release for a lazy script: it loads once the
   * tag's position in the document scrolls near the viewport, instead of on
   * interaction/timeout. Falls back to immediate load if IntersectionObserver isn't
   * available.
   * @param {HTMLElement} tag
   * @param {string} src
   */
  function observeLazyScript(tag, src) {
    if (typeof IntersectionObserver === "undefined") {
      loadWithDelay(tag, src);
      return;
    }

    // A script tag has no box to intersect, so observe its next element sibling if
    // present, otherwise its parent — this approximates "near this point in the page".
    const target = tag.nextElementSibling || tag.parentElement;
    if (!target) {
      loadWithDelay(tag, src);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          loadWithDelay(tag, src);
        });
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(target);
    lazyObservers.push({ tag, src, observer });
  }

  // Tracks lazy scripts still pending observation, so the window.load release can
  // disconnect their observers and force-load them if the user never scrolls there.
  const lazyObservers = [];

  // Tracks which script[delay] tags have already been handed off (scheduled or
  // observed), so runScripts() is safe to call more than once — e.g. once from the
  // interaction/timeout trigger and again from the window.load release.
  const tagsHandled = new WeakSet();

  /**
   * Finds and loads all scripts with the delay attribute
   * This is the main entry point called on user interaction or timeout
   */
  function runScripts() {
    document.querySelectorAll("script[delay]").forEach(function (scriptTag) {
      if (tagsHandled.has(scriptTag)) return;
      tagsHandled.add(scriptTag);

      const src = scriptTag.getAttribute("delay");
      const isLazy = scriptTag.getAttribute("data-lazy-load") === "true";

      if (isLazy) {
        observeLazyScript(scriptTag, src);
      } else {
        loadWithDelay(scriptTag, src);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // DOM interception — 1:1 port of the captured Yottaa sequencing (see header).
  // ---------------------------------------------------------------------------

  // Captured, neutralized scripts waiting for the window.load release.
  // Each entry: { node, src, type, host, delayValue }.
  const capturedScripts = [];

  // Hosts whose first captured resource has been released — further resources from
  // these hosts release with no offset, matching Yottaa (a host in scheduling.hosts
  // is skipped by the spread assignment; its captures keep the default "none" delay).
  const releasedHosts = [];

  // The cumulative spread counter (Yottaa g.scheduling.last, starting at 100).
  let spreadLast = spreadBaseMs;

  // Set when window.load fires; drives the immediate-release path and the 6s expiry.
  let loadFiredAt = null;

  /**
   * Whether the interception scheduler is still live. Mirrors Yottaa's expiry check
   * (now > onLoadTime + lastScheduleTime → "Scheduling has expired").
   * @returns {boolean}
   */
  function schedulingActive() {
    return loadFiredAt === null || Date.now() <= loadFiredAt + scheduleWindowMs;
  }

  /**
   * Assigns a spread offset for a captured script's host: the counter grows one step
   * per captured resource whose host hasn't started loading yet, exactly like Yottaa's
   * jb() (`last += step` when the host isn't in scheduling.hosts). Resources from a
   * host that has already started release with no offset.
   * @param {string} host
   * @returns {number}
   */
  function assignSpreadOffset(host) {
    if (releasedHosts.indexOf(host) !== -1) return 0;
    spreadLast += spreadStepMs;
    return spreadLast;
  }

  /**
   * Neutralizes a vendor <script> in place, exactly like Yottaa's sequence scrape:
   * stash and remove the src, stash and rewrite the type to a non-executable marker.
   * The node keeps its identity, attributes, and event handlers. Because it enters
   * (or sits in) the document with no src and no children, the browser never prepares
   * it — so restoreScript() can re-arm it later on the same node.
   *
   * Returns false (fail open — let the real insertion proceed unmodified) for anything
   * that shouldn't be touched: non-scripts, our own tagged/injected scripts, inline
   * scripts, exempt srcs, already-neutralized nodes, or an expired scheduler.
   * @param {Node} node
   * @returns {boolean} true if the node was captured
   */
  function captureScript(node) {
    try {
      if (!node || node.tagName !== "SCRIPT") return false;
      if (node.__delayManaged || node.__delayCaptured) return false;
      if (node.hasAttribute("delay") || node.hasAttribute("await")) return false; // own tags
      if (!schedulingActive()) return false; // Yottaa: "Scheduling has expired"
      const src = node.getAttribute("src");
      if (!src || isExempt(src)) return false;
      // Only classic/empty types are interceptable the same way Yottaa's scrape
      // handles them; anything exotic passes through.
      const type = node.getAttribute("type") || "";
      if (type && type !== "text/javascript" && type !== "module") return false;

      node.__delayCaptured = true;
      node.setAttribute("data-delay-type", type);
      node.setAttribute("data-delay-src", src);
      node.removeAttribute("src");
      node.setAttribute("type", NEUTRALIZED_TYPE);

      const host = getHost(src);
      const entry = { node, src, type, host, delayValue: assignSpreadOffset(host) };
      capturedScripts.push(entry);

      // Captured after window.load: release immediately on its offset (Yottaa
      // executes a post-load capture's delay timer right away).
      if (loadFiredAt !== null) releaseCapturedScript(entry);
      return true;
    } catch (e) {
      return false; // fail open: the real insertion proceeds untouched
    }
  }

  /**
   * Releases one captured script after its spread offset: restores the original type,
   * then re-sets the src ATTRIBUTE — on a connected script element that previously had
   * no src attribute, that re-triggers preparation on the vendor's own node, so its
   * own onload/onerror handlers fire naturally (Yottaa's `attributesrc` execute path).
   * A node the vendor removed from the DOM in the meantime is re-appended to body
   * first, matching Yottaa's re-insertion fallback.
   * @param {{node: HTMLElement, src: string, type: string, host: string, delayValue: number}} entry
   */
  function releaseCapturedScript(entry) {
    if (entry.released) return;
    entry.released = true;

    setTimeout(function () {
      if (releasedHosts.indexOf(entry.host) === -1) releasedHosts.push(entry.host);

      const node = entry.node;
      if (entry.type) {
        node.setAttribute("type", entry.type);
      } else {
        node.removeAttribute("type");
      }
      node.removeAttribute("data-delay-type");
      node.removeAttribute("data-delay-src");

      if (!node.isConnected) {
        node.setAttribute("src", entry.src);
        insertRealScript(node);
      } else {
        node.setAttribute("src", entry.src);
      }
    }, entry.delayValue);
  }

  /**
   * Patches appendChild/insertBefore/append on an insertion root so every <script>
   * passing through is neutralized (in place) by captureScript() before the real DOM
   * method inserts it. The node is ALWAYS inserted — capture only disarms it.
   * Yottaa patches document.body this way; head is included here because this theme's
   * third parties mostly insert into head, and pre-insertion neutralization is strictly
   * more reliable than the post-insertion observer.
   * @param {HTMLElement} root - document.head or document.body
   */
  function patchInsertionPoint(root) {
    if (!root || root.__delayPatched) return;
    root.__delayPatched = true;

    const realAppendChild = root.appendChild.bind(root);
    const realInsertBefore = root.insertBefore.bind(root);
    const realAppend = root.append ? root.append.bind(root) : null;

    // loadScript() always targets document.body — give it the pre-patch reference once
    // body is the root being patched, so its own insertions skip capture entirely.
    if (root === document.body) insertRealScript = realAppendChild;

    root.appendChild = function (node) {
      captureScript(node);
      return realAppendChild(node);
    };

    root.insertBefore = function (node, ref) {
      captureScript(node);
      return realInsertBefore(node, ref);
    };

    if (realAppend) {
      root.append = function (...nodes) {
        nodes.forEach((n) => captureScript(n));
        realAppend(...nodes);
      };
    }
  }

  /**
   * Window.load: this is the interception release point (Yottaa executes its delayed
   * list on `core/load`) AND the tagged-script sweep — lazy observers are force-loaded,
   * the interaction/timeout trigger is force-run if it hasn't fired, and orphaned
   * script[await] tags whose chain never fired are picked up. Nothing waits forever.
   */
  function onWindowLoad() {
    loadFiredAt = Date.now();

    capturedScripts.forEach(releaseCapturedScript);

    while (lazyObservers.length) {
      const pending = lazyObservers.pop();
      pending.observer.disconnect();
      loadWithDelay(pending.tag, pending.src);
    }
    runScripts();
    document.querySelectorAll("script[await]").forEach(function (scriptTag) {
      if (tagsHandled.has(scriptTag)) return;
      tagsHandled.add(scriptTag);
      loadWithDelay(scriptTag, scriptTag.getAttribute("await"));
    });
  }

  // --- DOM interception setup ---
  // Runs as the very last step of init, after every function above is defined, so
  // there's no risk of an insertion racing the patches before they're ready.
  //
  // A document-wide MutationObserver backstops the method patches, exactly like
  // Yottaa's sequence scrape observer: it catches scripts inserted into any OTHER
  // parent (a div container, a template fragment) and patches body the moment it
  // exists (delay.js runs in head, before body is parsed). Post-insertion capture is
  // best-effort — a classic script inserted with a src may already be committed by the
  // time the observer runs — which is the same limitation the Yottaa runtime has, and
  // it fails open (the script simply loads normally).
  patchInsertionPoint(document.head);
  if (document.body) patchInsertionPoint(document.body);

  const scrapeObserver = new MutationObserver(function (mutations) {
    if (document.body) patchInsertionPoint(document.body);
    for (let i = 0; i < mutations.length; i++) {
      const added = mutations[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        const node = added[j];
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "SCRIPT") {
          captureScript(node);
        }
      }
    }
  });
  scrapeObserver.observe(document.documentElement, { childList: true, subtree: true });

  // Once the scheduling window has expired, the observer has nothing left to do
  // (captureScript refuses expired captures) — disconnect it, like Yottaa's
  // scrapeLevel "none" teardown.
  window.addEventListener(
    "load",
    function () {
      setTimeout(function () {
        scrapeObserver.disconnect();
      }, scheduleWindowMs);
    },
    { once: true }
  );
})(this);
